"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Quote } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  author: "Dr. Aris Thorne",
  position: "Chief Medical Officer",
  quote: "The Eir platform has revolutionized our diagnostic workflow, providing the precision intelligence we needed to optimize patient outcomes.",
  impactLabel: "Clinical Precision",
  avatarImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1643670/img/avatar_aris_thorne.png",
  initials: "AT"
}, {
  author: "Sarah Chen",
  position: "Health System CEO",
  quote: "Partnering with Eirion for fractional CTO services transformed our legacy infrastructure into a high-performance digital ecosystem.",
  impactLabel: "Strategic Growth",
  avatarImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1643670/img/avatar_sarah_chen.png",
  initials: "SC"
}, {
  author: "Marcus Vane",
  position: "Lab Operations Director",
  quote: "Integrating Eirion's genetics lab services allowed us to scale our testing capacity by 300% without compromising on data integrity.",
  impactLabel: "Diagnostic Velocity",
  avatarImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1643670/img/avatar_marcus_vane.png",
  initials: "MV"
}, {
  author: "Elena Rodriguez",
  position: "Digital Health Lead",
  quote: "Their custom software development team doesn't just write code; they build clinical solutions that truly understand the provider's journey.",
  impactLabel: "Digital Transformation",
  avatarImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1643670/img/avatar_elena_rodriguez.png",
  initials: "ER"
}];
export default function SuccessStories() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="225566" id="voices-of-innovation" className="bg-background text-foreground py-24 md:py-32 overflow-hidden" ref={ref}>
      <div className={`container mx-auto px-4 md:px-8 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="max-w-3xl mb-16">
          <h2 className="text-3xl md:text-5xl font-default font-bold text-foreground tracking-tighter mb-6">
            Voices of Innovation
          </h2>
          <p className="text-lg text-muted-foreground font-sans max-w-2xl">
            Hear from the clinical leaders and technology pioneers transforming patient care 
            through Eirion's precision intelligence and strategic leadership.
          </p>
        </div>

        <div className="relative">
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4 md:-ml-6">
              {testimonials.map((testimonial, i) => <CarouselItem key={i} data-index={i} className="pl-4 md:pl-6 md:basis-1/2 lg:basis-1/2 xl:basis-1/3">
                  <Card className="h-full bg-card/50 backdrop-blur-xl shadow-lg hover:shadow-primary/5 transition-all duration-500 border-white/10 rounded-md relative overflow-hidden group">
                    {}
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
                    
                    <CardContent className="p-8 md:p-10 flex flex-col h-full justify-between relative z-10">
                      <div>
                        <div className="flex justify-between items-start mb-8">
                          <Quote className="size-10 text-primary/30" />
                          <Badge variant="secondary" className="font-mono text-[10px] uppercase tracking-widest px-3 py-1 bg-secondary/10 text-secondary border border-secondary/20 hover:bg-secondary/20 transition-colors">
                            {testimonial.impactLabel}
                          </Badge>
                        </div>
                        
                        <blockquote className="mb-10">
                          <p className="font-serif italic text-xl md:text-2xl leading-relaxed text-foreground/90">
                            "{testimonial.quote}"
                          </p>
                        </blockquote>
                      </div>

                      <div className="flex items-center gap-4 mt-auto pt-6 border-t border-white/5">
                        <SafeHydrate><Avatar className="size-14 border-2 border-primary/20 ring-2 ring-primary/10 ring-offset-2 ring-offset-card shadow-sm">
                          <AvatarImage src={testimonial.avatarImg} alt={testimonial.author} />
                          <AvatarFallback className="bg-primary/10 text-primary font-default font-bold">
                            {testimonial.initials}
                          </AvatarFallback>
                        </Avatar></SafeHydrate>
                        <div>
                          <p className="font-default font-bold text-foreground text-base tracking-tight">
                            {testimonial.author}
                          </p>
                          <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider">
                            {testimonial.position}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>)}
            </CarouselContent>
            
            <div className="flex justify-end gap-4 mt-12 md:mt-16 pr-4">
              <CarouselPrevious className="relative inset-auto translate-x-0 translate-y-0 size-12 bg-card/50 border-white/10 text-foreground hover:bg-primary hover:text-primary-foreground shadow-lg backdrop-blur-md rounded-md" />
              <CarouselNext className="relative inset-auto translate-x-0 translate-y-0 size-12 bg-card/50 border-white/10 text-foreground hover:bg-primary hover:text-primary-foreground shadow-lg backdrop-blur-md rounded-md" />
            </div>
          </Carousel>
        </div>
      </div>
    </section>;
}